import React, { useId, useState } from 'react';
import { IconChevronDown } from '@tabler/icons-react';
import type { DrillTarget } from '../../../lib/campaign-analytics/drill';
import type { AnalyticsQuestion } from '../../../lib/campaign-analytics/normalize';
import type { OverviewSection } from '../../../lib/campaign-analytics/overview';
import type { QuestionSummary } from '../../../lib/campaign-analytics/summarize';
import { QuestionCard, QuestionLine } from './QuestionCard';
import { SectionTextList } from './SectionTextList';
import { toneAt } from './chartTheme';

/** Props for {@link SectionRows}. */
export interface SectionRowsProps {
  sections: OverviewSection[];
  questions?: Map<string, AnalyticsQuestion>;
  onDrill?: (target: DrillTarget, label: string) => void;
  onAsk?: (prompt: string, summary: QuestionSummary) => void;
}

/**
 * One section as a single container: a header row that expands, inside the
 * same card, into its chart cards, its one-answer questions as a plain list,
 * and its text questions, so it is clear what belongs to the section.
 */
function SectionRow({ section, questions, onDrill, onAsk }: Omit<SectionRowsProps, 'sections'> & { section: OverviewSection }) {
  const id = useId();
  const [open, setOpen] = useState(false);
  const count = section.items.length + section.texts.length;
  const cards = section.items.filter(summary => summary.display === 'chart');
  const lines = section.items.filter(summary => summary.display !== 'chart');
  return (
    <section aria-label={section.label} className="overflow-hidden rounded-2xl border border-border/50 bg-surface light:bg-surface-raised">
      <button type="button" aria-expanded={open} aria-controls={id} onClick={() => setOpen(value => !value)}
        className="flex w-full items-center justify-between gap-4 bg-surface px-6 py-4 text-left transition-colors hover:bg-surface-hover focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-ring light:bg-surface-raised light:hover:bg-surface-raised-hover">
        <span className="min-w-0 truncate text-sm font-medium text-foreground">{section.label}</span>
        <span className="flex shrink-0 items-center gap-2 text-sm text-muted-foreground">
          {`${count} question${count === 1 ? '' : 's'}`}
          <IconChevronDown aria-hidden="true" className={`h-4 w-4 transition-transform motion-reduce:transition-none ${open ? 'rotate-180' : ''}`} />
        </span>
      </button>
      {open && (
        <div id={id} className="flex flex-col gap-4 border-t border-border/50 p-4">
          {cards.length > 0 && (
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              {cards.map((summary, index) => (
                <QuestionCard key={summary.fieldId} summary={summary} question={questions?.get(summary.fieldId)} onDrill={onDrill} onAsk={onAsk} tone={toneAt(index)} />
              ))}
            </div>
          )}
          {lines.length > 0 && (
            <ul className="divide-y divide-border/50">
              {lines.map(summary => <li key={summary.fieldId}><QuestionLine summary={summary} /></li>)}
            </ul>
          )}
          {section.texts.length > 0 && <SectionTextList items={section.texts} />}
        </div>
      )}
    </section>
  );
}

/** Every section as a collapsed row, so the page stays calm until someone looks deeper. */
export const SectionRows: React.FC<SectionRowsProps> = ({ sections, ...rest }) => (
  <div className="flex flex-col gap-3">
    {sections.map((section, index) => <SectionRow key={index} section={section} {...rest} />)}
  </div>
);
